import React, { useCallback, useEffect, useState } from "react";
import { Button } from "@component/button";
import { Input } from "@component/input";
import {
    BuildingIcon,
    PlusIcon,
    Trash2Icon,
    PencilIcon,
    CheckIcon,
    XIcon,
    FolderOpenIcon,
} from "lucide-react";
import SettingsTwoPanelLayout from "@component/setting-two-panel";
import { useSnackbar } from "@hook/use-snackbar";
import { Company, SubCompany } from "@type/company";

interface Props {
    company: Company;
    onCompaniesChange?: (companies: SubCompany[]) => void;
}

export default function SettingCompanyDepartmentSection({
    company,
    onCompaniesChange,
}: Props) {
    const snackbar = useSnackbar();
    const [companies, setCompanies] = useState<SubCompany[]>([]);
    const [selectedCompanyIndex, setSelectedCompanyIndex] = useState<number | null>(0);
    const [selectedDepartmentIndex, setSelectedDepartmentIndex] = useState<number | null>(null);
    const [departmentEditingValue, setDepartmentEditingValue] = useState<string>("");
    const [showNewCompanyInput, setShowNewCompanyInput] = useState<boolean>(false);
    const [showNewDepartmentInput, setShowNewDepartmentInput] = useState<boolean>(false);
    const [newCompanyName, setNewCompanyName] = useState("");
    const [newDepartmentName, setNewDepartmentName] = useState<string>("")
    const [editingCompanyIndex, setEditingCompanyIndex] = useState<number | null>(null);
    const [editCompanyValue, setEditCompanyValue] = useState<string>("");

    useEffect(() => {
        if (company) {
            if (!company.company_metadata) {
                setCompanies([
                    {
                        name: company.company_name,
                        is_main: true,
                        departments: [],
                    },
                ]);
            } else {
                setCompanies((company.company_metadata?.companies || []));
            }
        }
    }, [company])

    const addCompany = useCallback(() => {
        if (newCompanyName.trim() === "") {
            snackbar.showError("Company name cannot be empty.");
            return;
        }

        if (companies.find(c => c.name.toLowerCase() === newCompanyName.trim().toLowerCase())) {
            snackbar.showError("Company name already exists.");
            return;
        }

        const newCompany: SubCompany = {
            name: newCompanyName.trim(),
            is_main: false,
            departments: [],
        };

        const updatedCompanies = [...companies, newCompany];
        console.log("Updated Companies:", updatedCompanies);
        setCompanies(updatedCompanies);
        setNewCompanyName("");
        setShowNewCompanyInput(false);
        if (onCompaniesChange) {
            onCompaniesChange(updatedCompanies);
        }
    }, [newCompanyName, companies, onCompaniesChange, snackbar])

    const addDepartment = useCallback((companyIndex: number) => {
        if (newDepartmentName.trim() === "") {
            snackbar.showError("Department name cannot be empty.");
            return;
        }

        const updatedCompanies = companies.map((company, index) => {
            if (index === companyIndex) {
                return {
                    ...company,
                    departments: [...company.departments, newDepartmentName.trim()],
                };
            }
            return company;
        });

        setCompanies(updatedCompanies);
        setNewDepartmentName("");
        setShowNewDepartmentInput(false);
        if (onCompaniesChange) {
            onCompaniesChange(updatedCompanies);
        }
    }, [newDepartmentName, companies, onCompaniesChange, snackbar])

    const editCompany = useCallback((index: number) => {
        if (editCompanyValue.trim() === "") {
            snackbar.showError("Company name cannot be empty.");
            return;
        }

        if (companies.find((c, i) => c.name.toLowerCase() === editCompanyValue.trim().toLowerCase() && i !== index)) {
            snackbar.showError("Company name already exists.");
            return;
        }

        const updatedCompanies = companies.map((company, i) =>
            i === index ? { ...company, name: editCompanyValue.trim() } : company
        );

        setCompanies(updatedCompanies);
        setEditingCompanyIndex(null);
        setEditCompanyValue("");
        if (onCompaniesChange) {
            onCompaniesChange(updatedCompanies);
        }
    }, [editCompanyValue, companies, onCompaniesChange, snackbar])

    const editDepartment = useCallback((companyIndex: number, departmentIndex: number) => {
        if (departmentEditingValue.trim() === "") {
            snackbar.showError("Department name cannot be empty.");
            return;
        }

        const updatedCompanies = companies.map((company, cIndex) => {
            if (cIndex === companyIndex) {
                const updatedDepartments = company.departments.map((dept, dIndex) =>
                    dIndex === departmentIndex ? departmentEditingValue.trim() : dept
                );
                return { ...company, departments: updatedDepartments };
            }
            return company;
        });

        setCompanies(updatedCompanies);
        setSelectedDepartmentIndex(null);
        setDepartmentEditingValue("");
        if (onCompaniesChange) {
            onCompaniesChange(updatedCompanies);
        }
    }, [departmentEditingValue, companies, onCompaniesChange, snackbar]);

    const cancelEdit = useCallback(() => {
        setEditingCompanyIndex(null);
        setEditCompanyValue("");
    }, []);

    const deleteCompany = useCallback((index: number) => {
        const updatedCompanies = companies.filter((_, i) => i !== index);
        setCompanies(updatedCompanies);
        if (onCompaniesChange) {
            onCompaniesChange(updatedCompanies);
        }
    }, [companies, onCompaniesChange]);

    const deleteDepartment = useCallback((companyIndex: number, departmentIndex: number) => {
        // department can't be empty at least one department
        if (companies[companyIndex].departments.length <= 1) {
            snackbar.showError("Each company must have at least one department.");
            return;
        }

        const updatedCompanies = companies.map((company, cIndex) => {
            if (cIndex === companyIndex) {
                const updatedDepartments = company.departments.filter((_, dIndex) => dIndex !== departmentIndex);
                return { ...company, departments: updatedDepartments };
            }
            return company;
        });

        setCompanies(updatedCompanies);
        if (onCompaniesChange) {
            onCompaniesChange(updatedCompanies);
        }
    }, [companies, onCompaniesChange]);

    return (
        <SettingsTwoPanelLayout
            title="Companies and Departments"
            description="Manage your company and department structure to streamline job creation and hiring workflows."
        >
            <div className="grid grid-cols-2 gap-6">
                {/* Left Column - Companies */}
                <div className="space-y-3">
                    <div className="flex items-center justify-between pb-2 border-b border-gray-200">
                        <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                            Companies
                        </h3>
                        {/* Add Company Button - Aligned with Header */}
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setShowNewCompanyInput(true)}
                            disabled={showNewCompanyInput}
                            className="text-blue-600 hover:bg-blue-50 hover:text-blue-700 h-7 px-2"
                        >
                            <PlusIcon className="w-4 h-4 mr-1" />
                            Add Company
                        </Button>
                    </div>

                    <div
                        className="space-y-1 max-h-[400px] overflow-y-auto"
                        style={{ paddingRight: "4px", paddingLeft: "4px" }}
                    >
                        {companies.map((company, companyIndex) => (
                            <div
                                key={companyIndex}
                                onClick={() => {
                                    setSelectedCompanyIndex(companyIndex);
                                    setSelectedDepartmentIndex(null);
                                }}
                                className={`flex items-center justify-between px-3 py-2 rounded-md cursor-pointer transition-colors group hover:bg-gray-50 ${selectedCompanyIndex === companyIndex ? "ring-2 ring-blue-500" : ""
                                    }`}
                                style={{ marginTop: "4px", marginBottom: "4px" }}
                            >
                                {editingCompanyIndex === companyIndex ? (
                                    <div className="flex items-center space-x-2 flex-1">
                                        <Input
                                            value={editCompanyValue}
                                            onChange={(e) => setEditCompanyValue(e.target.value)}
                                            className="flex-1"
                                            autoFocus
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") editCompany(companyIndex);
                                                if (e.key === "Escape") cancelEdit();
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                        />

                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                editCompany(companyIndex);
                                            }}
                                            className="h-8 w-8 p-0 rounded-md hover:bg-green-50"
                                        >
                                            <CheckIcon className="w-4 h-4 text-green-600" />
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                cancelEdit();
                                            }}
                                            className="h-8 w-8 p-0 rounded-md hover:bg-red-50"
                                        >
                                            <XIcon className="w-4 h-4 text-gray-500" />
                                        </Button>
                                    </div>
                                ) : (
                                    <>
                                        <div
                                            className="flex items-center space-x-2 flex-1"
                                            style={{ marginTop: "4px", marginBottom: "4px" }}
                                        >
                                            <BuildingIcon className="w-4 h-4 text-gray-500" />

                                            <span className="text-sm text-gray-900">
                                                {company.name}
                                            </span>
                                            {company.is_main && (
                                                <div className="flex items-center space-x-1 bg-blue-100 text-blue-700 px-2 py-0.5 rounded-2xl text-xs font-medium">
                                                    <span>Main</span>
                                                </div>
                                            )}
                                        </div>

                                        {!company.is_main && (
                                            <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setEditingCompanyIndex(companyIndex);
                                                        setEditCompanyValue(company.name);
                                                    }}
                                                    className="h-7 w-7 p-0 hover:bg-gray-200"
                                                >
                                                    <PencilIcon className="w-3.5 h-3.5 text-gray-500" />
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        deleteCompany(companyIndex);
                                                    }}
                                                    className="h-7 w-7 p-0 hover:bg-gray-200"
                                                >
                                                    <Trash2Icon className="w-3.5 h-3.5 text-gray-500" />
                                                </Button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Add New Company Input - Inline */}
                    {showNewCompanyInput && (
                        <div
                            className="flex items-center space-x-2"
                            style={{ paddingRight: "12px", paddingLeft: "12px" }}
                        >
                            <Input
                                placeholder="Company name"
                                value={newCompanyName}
                                onChange={(e) => setNewCompanyName(e.target.value)}
                                className="flex-1"
                                autoFocus
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") addCompany();
                                    if (e.key === "Escape") {
                                        setShowNewCompanyInput(false);
                                        setNewCompanyName("");
                                    }
                                }}
                            />

                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={addCompany}
                                className="h-8 w-8 p-0 rounded-md hover:bg-green-50"
                            >
                                <CheckIcon className="w-4 h-4 text-green-600" />
                            </Button>
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                    setShowNewCompanyInput(false);
                                    setNewCompanyName("");
                                }}
                                className="h-8 w-8 p-0 rounded-md hover:bg-red-50"
                            >
                                <XIcon className="w-4 h-4 text-gray-500" />
                            </Button>
                        </div>
                    )}
                </div>

                {/* Right Column - Departments */}
                <div className="space-y-3">
                    <div className="flex items-center justify-between pb-2 border-b border-gray-200">
                        <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                            Departments
                        </h3>
                        {/* Add Department Button - Aligned with Header */}
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setShowNewDepartmentInput(true)}
                            disabled={showNewDepartmentInput || selectedCompanyIndex === null}
                            className="text-blue-600 hover:bg-blue-50 hover:text-blue-700 h-7 px-2"
                        >
                            <PlusIcon className="w-4 h-4 mr-1" />
                            Add Department
                        </Button>
                    </div>

                    {selectedCompanyIndex !== null && companies[selectedCompanyIndex] && (
                        <div
                            className="space-y-1 max-h-[400px] overflow-y-auto"
                            style={{ paddingRight: "4px", paddingLeft: "4px" }}
                        >
                            {(companies[selectedCompanyIndex].departments || []).length === 0 && !showNewDepartmentInput && (
                                <div className="px-3 py-8 text-center">
                                    <div className="flex justify-center mb-3">
                                        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                                            <FolderOpenIcon className="w-6 h-6 text-gray-400" />
                                        </div>
                                    </div>
                                    <p className="text-sm text-gray-500">
                                        No departments added.
                                    </p>
                                    <p className="text-xs text-gray-400 mt-1">
                                        Each company must have at least one department.
                                        <br />
                                        Click "Add Department" to get started.
                                    </p>
                                </div>
                            )}

                            {(companies[selectedCompanyIndex].departments || []).map((department, departmentIndex) => (
                                <div
                                    key={departmentIndex}
                                    className="flex items-center justify-between px-3 py-2 rounded-md hover:bg-gray-50 group"
                                >
                                    {selectedDepartmentIndex === departmentIndex ? (
                                        <div className="flex items-center space-x-2 flex-1">
                                            <Input
                                                value={departmentEditingValue}
                                                onChange={(e) => setDepartmentEditingValue(e.target.value)}
                                                className="flex-1"
                                                autoFocus
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        editDepartment(selectedCompanyIndex, departmentIndex);
                                                    }
                                                    if (e.key === "Escape") {
                                                        setSelectedDepartmentIndex(null);
                                                        setDepartmentEditingValue("");
                                                    }
                                                }}
                                            />

                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => {
                                                    editDepartment(selectedCompanyIndex, departmentIndex);
                                                }}
                                                className="h-8 w-8 p-0 rounded-md hover:bg-green-50"
                                            >
                                                <CheckIcon className="w-4 h-4 text-green-600" />
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => {
                                                    setSelectedDepartmentIndex(null);
                                                    setDepartmentEditingValue("");
                                                }}
                                                className="h-8 w-8 p-0 rounded-md hover:bg-red-50"
                                            >
                                                <XIcon className="w-4 h-4 text-gray-500" />
                                            </Button>
                                        </div>
                                    ) : (
                                        <>
                                            <span className="text-sm text-gray-700">
                                                {department}
                                            </span>
                                            <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => {
                                                        setSelectedDepartmentIndex(departmentIndex);
                                                        setDepartmentEditingValue(department);
                                                    }}
                                                    className="h-7 w-7 p-0 hover:bg-gray-200"
                                                >
                                                    <PencilIcon className="w-3.5 h-3.5 text-gray-500" />
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => {
                                                        deleteDepartment(selectedCompanyIndex, departmentIndex);
                                                    }}
                                                    className="h-7 w-7 p-0 hover:bg-gray-200"
                                                >
                                                    <Trash2Icon className="w-3.5 h-3.5 text-gray-500" />
                                                </Button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}

                            {/* Add Department Input */}
                            {showNewDepartmentInput && (
                                <div
                                    className="flex items-center space-x-2"
                                    style={{
                                        marginTop: "4px",
                                        marginBottom: "4px",
                                        paddingTop: "8px",
                                        paddingRight: "12px",
                                        paddingBottom: "8px",
                                        paddingLeft: "12px",
                                    }}
                                >
                                    <Input
                                        placeholder="Department name"
                                        value={newDepartmentName}
                                        onChange={(e) =>
                                            setNewDepartmentName(e.target.value)
                                        }
                                        className="flex-1"
                                        autoFocus
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") addDepartment(selectedCompanyIndex);
                                            if (e.key === "Escape") {
                                                setShowNewDepartmentInput(false);
                                                setNewDepartmentName("");
                                            }
                                        }}
                                    />

                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => addDepartment(selectedCompanyIndex)}
                                        className="h-8 w-8 p-0 rounded-md hover:bg-green-50"
                                    >
                                        <CheckIcon className="w-4 h-4 text-green-600" />
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => {
                                            setShowNewDepartmentInput(false);
                                            setNewDepartmentName("");
                                        }}
                                        className="h-8 w-8 p-0 rounded-md hover:bg-red-50"
                                    >
                                        <XIcon className="w-4 h-4 text-gray-500" />
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </SettingsTwoPanelLayout>
    );
}
